import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

const Hero = () => {
  const texts = ["INTEGRE.", "AUTOMATIZE.", "ESCALE."];
  const [displayText, setDisplayText] = useState("");
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  // --- Efeito de digitação ---
  useEffect(() => {
    if (index >= texts.length) return;
    if (subIndex === texts[index].length + 1 && !deleting) {
      setTimeout(() => setDeleting(true), 1000);
      return;
    }
    if (subIndex === 0 && deleting) {
      setDeleting(false);
      setIndex((prev) => (prev + 1) % texts.length);
      return;
    }
    const timeout = setTimeout(
      () => {
        setSubIndex((prev) => prev + (deleting ? -1 : 1));
      },
      deleting ? 50 : 100
    );
    setDisplayText(texts[index].substring(0, subIndex));
    return () => clearTimeout(timeout);
  }, [subIndex, index, deleting, texts]);

  // --- Fonte Orbitron (somente pro texto animado) ---
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Orbitron:wght@600;700;800;900&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center text-center overflow-hidden"
    >
      {/* === FUNDO GRID VIOLETA === */}
      <div className="absolute inset-0 -z-20 overflow-hidden animate-bg-float">
        <div
          className="absolute inset-0 opacity-[0.25] animate-grid-move"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.45] animate-grid-move-rev"
          style={{
            backgroundImage:
              "linear-gradient(rgba(168,85,247,0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.25) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(100%_100%_at_50%_50%,transparent_65%,rgba(30,0,60,0.55)_100%)]" />
      </div>

      {/* === PARTÍCULAS VIOLETAS === */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {[...Array(18)].map((_, i) => (
          <div
            key={i}
            className="absolute w-[3px] h-[3px] bg-[#a855f780] rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.6 + 0.2,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      {/* === CONTEÚDO CENTRAL === */}
      <div className="relative px-4 sm:px-8 w-full max-w-6xl">
        <p className="text-[#white] text-sm sm:text-base mb-4 tracking-[0.25em] uppercase">
          AUTOMAÇÃO INTELIGENTE // INTEGRAÇÃO DIGITAL
        </p>

        <div className="relative flex justify-center">
          <div className="absolute -z-10 top-1/2 -translate-y-1/2 w-[500px] h-[200px] bg-[#7c3aed40] blur-[120px] rounded-full opacity-60"></div>

          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-extrabold mb-8 leading-tight tracking-wide relative text-foreground">
            {/* Orbitron apenas aqui ↓ */}
            <span
              className={`block font-[Orbitron] text-transparent bg-clip-text bg-gradient-to-r from-[#00ffaa] via-[#7C3AED] to-[#00ffaa]
                bg-[length:220%_auto] animate-gradient-flow transition-all duration-500 ${
                  displayText ? "animate-text-glow" : ""
                }`}
              style={{
                textShadow:
                  "0 0 10px rgba(0,255,170,0.4), 0 0 20px rgba(124,58,237,0.6)",
              }}
            >
              {displayText}
              <span className="animate-cursor text-[#00ffaa]">|</span>
            </span>

            <span className="pointer-events-none absolute inset-0 [mask-image:linear-gradient(to_bottom,transparent,black_20%,black_80%,transparent)]">
              <span className="absolute -inset-x-16 top-1/3 h-12 bg-gradient-to-r from-transparent via-white/20 to-transparent blur-2xl animate-shine" />
            </span>
          </h1>
        </div>

        <p className="text-white text-lg sm:text-xl mb-10 max-w-xl mx-auto">
          Sua empresa conectada, com fluxos inteligentes e automações sem
          esforço.
        </p>

        <div className="group relative inline-block before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/10 before:to-transparent before:rounded-full before:pointer-events-none">
          <a
            href="https://flow.ascconecta.com.br/login"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              size="lg"
              className="relative overflow-hidden border border-white/60 text-white text-lg px-8 py-6 rounded-full font-semibold tracking-wide bg-transparent backdrop-blur-sm hover:bg-white/5 transition-all duration-500 shadow-[0_0_12px_rgba(168,85,247,0.4)] hover:shadow-[0_0_20px_rgba(168,85,247,0.6)]"
            >
              Ver Demonstração
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </a>
        </div>
      </div>

      <div className="absolute bottom-0 w-full h-32 bg-gradient-to-t from-[#7c3aed30] to-transparent blur-2xl"></div>

      <style>{`
        @keyframes gradientFlow {
          0% { background-position: 0% 50%; filter: brightness(1); }
          50% { background-position: 100% 50%; filter: brightness(1.1); }
          100% { background-position: 0% 50%; filter: brightness(1); }
        }
        .animate-gradient-flow { animation: gradientFlow 5s ease-in-out infinite; }

        @keyframes gridMove {
          from { background-position: 0 0, 0 0; }
          to { background-position: 32px 32px, 32px 32px; }
        }
        .animate-grid-move { animation: gridMove 18s linear infinite; }
        .animate-grid-move-rev { animation: gridMove 24s linear infinite reverse; }

        @keyframes shine {
          0%, 85% { transform: translateX(-120%); opacity: 0; }
          90% { opacity: .9; }
          100% { transform: translateX(120%); opacity: 0; }
        }
        .animate-shine { animation: shine 9s ease-in-out infinite; }

        @keyframes blink {
          0%, 50%, 100% { opacity: 1; }
          25%, 75% { opacity: 0; }
        }
        .animate-cursor { animation: blink 1s infinite; }

        @keyframes textGlow {
          0%, 100% { filter: drop-shadow(0 0 10px rgba(0,255,170,0.5)); }
          50% { filter: drop-shadow(0 0 16px rgba(124,58,237,0.7)); }
        }
        .animate-text-glow {
          animation: textGlow 1.6s ease-in-out infinite;
        }

        @keyframes backgroundFloat {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }
        .animate-bg-float {
          animation: backgroundFloat 20s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Hero;
